<script lang="ts">
export default {
  name: 'PolicyEditFormPanel',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { addCommas, removeCommas } from '@/utilities/Number';
import AppSelect from '@/components/v2/AppSelect.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { FormSchemeType, PolicyRuleViolationFlags, ValidFormFields } from '@/composables/usePolicyEditPending';
import type { PolicySource } from '@/types/PolicySource';

interface SelectOption {
  label: string;
  value: string;
}

const props = defineProps<{
  mode: 'policy' | 'line';
  originalForm: FormSchemeType;
  validFields: ValidFormFields;
  isDisabled: boolean;
  policySource?: PolicySource;
  syndicateOptions: SelectOption[];
  carrierOptions: SelectOption[];
  sideAOptions: string[];
  // Client-readiness rule flags; flagged fields get a warning border, label
  // dot, and hint until the violation is resolved (live, from current values).
  ruleViolations?: PolicyRuleViolationFlags;
}>();

const form = defineModel<FormSchemeType>('form', { required: true });

const isLine = computed(() => props.mode === 'line');

const allowCarrierUpdate = computed(() => !!props.policySource?.AllowCarrierUpdate);
const allowPremiumUpdate = computed(() => !!props.policySource?.AllowPremiumUpdate);

const showPremiumTotal = computed(() => isLine.value || allowPremiumUpdate.value);
const showCarrier = computed(() => !isLine.value && allowCarrierUpdate.value);

const attachmentPointAmount = computed({
  get: () => (form.value.attachmentApplicable ? addCommas(form.value.attachmentPointAmount ?? '') : ''),
  set: (val: string) => {
    form.value.attachmentPointAmount = removeCommas(val)?.toString() || '';
  },
});

const aggregateLimitAmount = computed({
  get: () => (form.value.aggregateLimitApplicable ? addCommas(form.value.aggregateLimitAmount ?? '') : ''),
  set: (val: string) => {
    form.value.aggregateLimitAmount = removeCommas(val)?.toString() ?? '';
  },
});

const occurrenceLimitAmount = computed({
  get: () => (form.value.occurrenceLimitApplicable ? addCommas(form.value.occurrenceLimitAmount ?? '') : ''),
  set: (val: string) => {
    form.value.occurrenceLimitAmount = removeCommas(val)?.toString() ?? '';
  },
});

const quotaShareTotal = computed({
  get: () => addCommas(form.value.quotaShareTotal ?? ''),
  set: (val: string) => {
    form.value.quotaShareTotal = removeCommas(val)?.toString() ?? '';
  },
});

const retentionAmount = computed({
  get: () => (form.value.retentionApplicable ? addCommas(form.value.retentionAmount ?? '') : ''),
  set: (val: string) => {
    form.value.retentionAmount = removeCommas(val)?.toString() ?? '';
  },
});

const premiumTotal = computed({
  get: () => addCommas(form.value.premiumTotal ?? ''),
  set: (val: string) => {
    form.value.premiumTotal = removeCommas(val)?.toString() ?? '';
  },
});

function changed(field: keyof FormSchemeType): boolean {
  return form.value[field] !== props.originalForm[field];
}

function isAmountResolved(applicable: boolean, amount: string | number | null | undefined): boolean {
  if (!applicable) return true;
  const s = amount == null ? '' : String(amount).trim();
  if (s === '') return false;
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) && n > 0;
}

const showLimitViolation = computed(() => {
  if (props.ruleViolations?.noLimit !== true) return false;
  const aggResolved = isAmountResolved(form.value.aggregateLimitApplicable, form.value.aggregateLimitAmount);
  const occResolved = isAmountResolved(form.value.occurrenceLimitApplicable, form.value.occurrenceLimitAmount);
  return !(aggResolved || occResolved);
});

const showRetentionViolation = computed(() => {
  if (props.ruleViolations?.invalidRetention !== true) return false;
  return !isAmountResolved(form.value.retentionApplicable, form.value.retentionAmount);
});

const showAttachmentViolation = computed(() => {
  if (props.ruleViolations?.invalidAttachment !== true) return false;
  return !isAmountResolved(form.value.attachmentApplicable, form.value.attachmentPointAmount);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-6">
      <div
        v-if="!isLine"
        class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Primary / Excess</span>
        </label>
        <AppSelect
          v-model="form.peFlag"
          :options="[
            { label: 'Primary', value: 'Primary' },
            { label: 'Excess', value: 'Excess' },
            { label: 'Fees', value: 'Fees' },
          ]"
          :disabled="isDisabled"
          :class="`h-10 w-full rounded-sm ${changed('peFlag') ? 'border-success' : ''}`" />
      </div>
      <div :class="isLine ? 'w-full' : 'w-1/2'">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Attachment Point</span>
          <span
            v-if="showAttachmentViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-attachment"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            inputmode="numeric"
            v-model="attachmentPointAmount"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.attachmentPointAmount,
              'border-warning': showAttachmentViolation && validFields.attachmentPointAmount,
              'border-success': changed('attachmentPointAmount') && validFields.attachmentPointAmount && !showAttachmentViolation,
            }"
            :disabled="!form.attachmentApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('attachmentApplicable') }"
              v-model="form.attachmentApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.attachmentApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.attachmentPointAmount && !showAttachmentViolation }">
          <span
            v-if="!validFields.attachmentPointAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showAttachmentViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-attachment"
            >Violating Rule</span
          >
        </label>
      </div>
    </div>

    <div class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Aggregate Limit</span>
          <span
            v-if="showLimitViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-aggregate"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            inputmode="numeric"
            v-model="aggregateLimitAmount"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.aggregateLimitAmount,
              'border-warning': showLimitViolation && validFields.aggregateLimitAmount,
              'border-success': changed('aggregateLimitAmount') && validFields.aggregateLimitAmount && !showLimitViolation,
            }"
            :disabled="!form.aggregateLimitApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('aggregateLimitApplicable') }"
              v-model="form.aggregateLimitApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.aggregateLimitApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.aggregateLimitAmount && !showLimitViolation }">
          <span
            v-if="!validFields.aggregateLimitAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showLimitViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-aggregate"
            >Violating Rule</span
          >
        </label>
      </div>
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Occurrence Limit</span>
          <span
            v-if="showLimitViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-occurrence"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            inputmode="numeric"
            v-model="occurrenceLimitAmount"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.occurrenceLimitAmount,
              'border-warning': showLimitViolation && validFields.occurrenceLimitAmount,
              'border-success': changed('occurrenceLimitAmount') && validFields.occurrenceLimitAmount && !showLimitViolation,
            }"
            :disabled="!form.occurrenceLimitApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('occurrenceLimitApplicable') }"
              v-model="form.occurrenceLimitApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.occurrenceLimitApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.occurrenceLimitAmount && !showLimitViolation }">
          <span
            v-if="!validFields.occurrenceLimitAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showLimitViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-occurrence"
            >Violating Rule</span
          >
        </label>
      </div>
    </div>

    <div class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Total Layer Limit</span>
        </label>
        <input
          type="text"
          v-model="quotaShareTotal"
          class="input input-md w-full border border-solid"
          :class="{
            'border-error': !validFields.quotaShareTotal,
            'border-success': changed('quotaShareTotal') && validFields.quotaShareTotal,
          }"
          :disabled="isDisabled" />
        <label
          class="label"
          :class="{ hidden: validFields.quotaShareTotal }">
          <span class="label-text-alt text-xs text-error">invalid amount</span>
        </label>
      </div>
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Retention</span>
          <span
            v-if="showRetentionViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-retention"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            v-model="retentionAmount"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.retentionAmount,
              'border-warning': showRetentionViolation && validFields.retentionAmount,
              'border-success': changed('retentionAmount') && validFields.retentionAmount && !showRetentionViolation,
            }"
            :disabled="!form.retentionApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('retentionApplicable') }"
              v-model="form.retentionApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.retentionApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.retentionAmount && !showRetentionViolation }">
          <span
            v-if="!validFields.retentionAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showRetentionViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-retention"
            >Violating Rule</span
          >
        </label>
      </div>
    </div>

    <div
      v-if="showPremiumTotal || showCarrier"
      class="flex gap-6">
      <div
        v-if="showPremiumTotal"
        class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Premium Total</span>
        </label>
        <input
          type="text"
          v-model="premiumTotal"
          class="input input-md w-full border border-solid"
          :class="{ 'border-success': changed('premiumTotal') }"
          :disabled="isDisabled" />
      </div>
      <div
        v-if="showCarrier"
        class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Carrier</span>
        </label>
        <Combobox
          v-model="form.carrierId"
          :options="carrierOptions"
          placeholder="Select a carrier..."
          :input-class="`h-10 w-full ${changed('carrierId') ? 'border-success' : ''}`"
          :disabled="isDisabled" />
      </div>
    </div>

    <div
      v-if="!isLine"
      class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Lead Syndicate</span>
        </label>
        <AppSelect
          v-model="form.leadSyndicateId"
          :options="syndicateOptions"
          :class="`h-10 w-full rounded-sm ${changed('leadSyndicateId') ? 'border-success' : ''}`"
          menu-class="w-[400px]"
          :disabled="isDisabled" />
      </div>
    </div>

    <div
      v-if="!isLine"
      class="flex gap-6">
      <div class="w-1/2">
        <div class="w-2/3">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Side A</span>
          </label>
          <AppSelect
            v-model="form.sideAValue"
            :options="sideAOptions.map((option) => ({ label: option, value: option }))"
            :class="`h-10 w-full rounded-sm ${changed('sideAValue') ? 'border-success' : ''}`"
            :disabled="isDisabled" />
        </div>
      </div>
      <div class="h-full w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Runoff</span>
        </label>
        <label class="label flex justify-normal gap-4">
          <input
            type="checkbox"
            v-model="form.runoff"
            class="toggle border border-solid"
            :class="{ 'border-success': changed('runoff') }"
            :disabled="isDisabled" />
        </label>
      </div>
    </div>
  </div>
</template>
